import { useMemo, useState } from 'react'
import type { CatalogIndex } from '../../catalog/types'
import { computeBalance } from '../../domain/balance'
import { compareQuestIds } from '../../domain/sort'
import { TAGS, type Quest, type Tag } from '../../domain/types'

export function QuestList({
  quests,
  catalog,
  selectedId,
  dirtyIds,
  problemIds,
  onSelect,
  onCreate,
}: {
  quests: Quest[]
  catalog: CatalogIndex
  selectedId: string | null
  dirtyIds: Set<string>
  problemIds: Set<string>
  onSelect: (id: string) => void
  onCreate: () => void
}) {
  const [query, setQuery] = useState('')
  const [tags, setTags] = useState<Set<Tag>>(new Set())
  const [maxGate, setMaxGate] = useState<number | null>(null)
  const [onlyDirty, setOnlyDirty] = useState(false)
  const [sortBy, setSortBy] = useState<'id' | 'gate'>('id')
  const [hideDisabled, setHideDisabled] = useState(false)

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return quests
      .map((quest) => ({ quest, balance: computeBalance(quest, catalog) }))
      .filter(({ quest }) => {
        if (tags.size > 0 && !tags.has(quest.tag)) return false
        if (maxGate !== null && quest.computed.gatingLevel > maxGate) return false
        if (onlyDirty && !dirtyIds.has(quest.id)) return false
        if (hideDisabled && quest.disabled) return false
        if (!needle) return true
        return (
          quest.id.toLowerCase().includes(needle) || quest.label.toLowerCase().includes(needle)
        )
      })
      .sort((a, b) =>
        sortBy === 'gate'
          ? // The order the in-game quest list uses.
            a.quest.computed.gatingLevel - b.quest.computed.gatingLevel ||
            compareQuestIds(a.quest.id, b.quest.id)
          : compareQuestIds(a.quest.id, b.quest.id),
      )
  }, [quests, catalog, query, tags, maxGate, onlyDirty, dirtyIds, sortBy, hideDisabled])

  function toggleTag(tag: Tag) {
    const next = new Set(tags)
    if (next.has(tag)) next.delete(tag)
    else next.add(tag)
    setTags(next)
  }

  return (
    <div className="flex w-96 shrink-0 flex-col border-r border-edge bg-ink-lift">
      <div className="flex flex-col gap-2 border-b border-edge p-3">
        <div className="flex gap-2">
          <input
            type="search"
            placeholder="Filter by id or label…"
            className="w-full rounded border border-edge px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <button
            type="button"
            onClick={onCreate}
            className="shrink-0 rounded bg-gold px-3 text-sm font-medium text-ink hover:bg-gold-lit"
          >
            New
          </button>
        </div>

        <div className="flex flex-wrap gap-1">
          {TAGS.map((tag) => (
            <button
              key={tag}
              type="button"
              onClick={() => toggleTag(tag)}
              className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${
                tags.has(tag)
                  ? 'border-gold bg-gold text-ink'
                  : 'border-edge text-dim hover:border-gold-shade'
              }`}
            >
              {tag}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-dim">
          <label className="flex items-center gap-1">
            gate ≤
            <input
              type="number"
              className="w-16 rounded border border-edge px-1 py-0.5 font-mono tabular-nums"
              value={maxGate ?? ''}
              placeholder="any"
              onChange={(event) =>
                setMaxGate(event.target.value === '' ? null : Number(event.target.value))
              }
            />
          </label>
          <label className="flex items-center gap-1">
            <input
              type="checkbox"
              checked={onlyDirty}
              onChange={(event) => setOnlyDirty(event.target.checked)}
            />
            unsaved
          </label>
          <label className="flex items-center gap-1">
            <input
              type="checkbox"
              checked={hideDisabled}
              onChange={(event) => setHideDisabled(event.target.checked)}
            />
            hide WIP
          </label>
          <label className="ml-auto flex items-center gap-1">
            sort
            <select
              aria-label="Sort quests by"
              className="rounded border border-edge bg-ink-lift px-1 py-0.5 text-xs"
              value={sortBy}
              onChange={(event) => setSortBy(event.target.value as 'id' | 'gate')}
            >
              <option value="id">quest id</option>
              <option value="gate">gate level</option>
            </select>
          </label>
          <span className="tabular-nums text-dim">{rows.length}</span>
        </div>
      </div>

      <ul className="min-h-0 flex-1 overflow-y-auto">
        {rows.map(({ quest, balance }) => {
          const active = quest.id === selectedId
          return (
            <li key={quest.id}>
              <button
                type="button"
                onClick={() => onSelect(quest.id)}
                className={`flex w-full flex-col gap-0.5 border-b border-edge px-3 py-2 text-left hover:bg-ink-high ${
                  active ? 'bg-panel hover:bg-panel-lit' : ''
                }`}
              >
                <div className="flex items-baseline gap-2">
                  <span
                    className={`min-w-0 flex-1 truncate text-sm font-medium ${
                      quest.disabled ? 'text-dim line-through' : 'text-parchment'
                    }`}
                  >
                    {quest.label || quest.id}
                  </span>
                  {quest.disabled && (
                    <span
                      className="shrink-0 rounded bg-warn-bg px-1 text-[10px] font-bold text-warn"
                      title="Work in progress — the game never offers this quest"
                    >
                      WIP
                    </span>
                  )}
                  {dirtyIds.has(quest.id) && (
                    <span className="shrink-0 rounded bg-gold px-1 text-[10px] font-bold text-ink">
                      UNSAVED
                    </span>
                  )}
                  {problemIds.has(quest.id) && (
                    <span
                      className="shrink-0 text-xs text-danger"
                      title="Part of a prereq cycle or missing reference"
                    >
                      ⚠
                    </span>
                  )}
                </div>
                <div className="flex items-baseline gap-2 font-mono text-[11px] text-dim">
                  <span className="min-w-0 flex-1 truncate">{quest.id}</span>
                  <span className="shrink-0 rounded bg-ink px-1">{quest.tag}</span>
                  <span className="shrink-0 tabular-nums" title="gatingLevel">
                    gate {quest.computed.gatingLevel}
                  </span>
                </div>
                <div className="font-mono text-[11px] tabular-nums text-dim">
                  {balance.estimatedValue} → {balance.actualValue}
                  {balance.ratio !== null && ` · ${Math.round(balance.ratio * 100)}%`}
                </div>
              </button>
            </li>
          )
        })}
        {rows.length === 0 && (
          <li className="p-4 text-sm text-dim">No quests match these filters.</li>
        )}
      </ul>
    </div>
  )
}
